// apps/fe/src/modules/catalog/hooks/use-category.ts

'use client';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { EventBus } from '@/shared/utils';
import { CatalogEvents } from '../events';
import { getCategoryFilteredData } from '../services/category-data-service';
import { CategoryNames, CategoryTypes } from '../types/catalog-category-data';
import { CourseType } from '../types/course';
import { ICourseData } from '../types/course-data';
import { CatalogFilters } from '../types/filter';
import { CourseDataFilters } from '../types/filtered-data';
import { Instrument } from '../types/instrument';
import { toggleFilterValue, updateInstrumentsFilter } from '../utils/common';
import { useAuthStore, useAuthenticatedFetch } from '@/auth';
import { createAuthenticatedCatalogServices } from '../services/api-services-authenticated';
import { Either, left } from '@shared-commons/utils';

/**
 * Props for the useCategory hook
 * @interface UseCategoryProps
 */
interface UseCategoryProps {
  /** EventBus instance for catalog-related events */
  eventBus: EventBus<CatalogEvents>;
  /** Current catalog filters configuration */
  filters: CatalogFilters;
  /** Optional category name to filter by */
  categoryName?: CategoryNames;
  /** Optional initial courses data */
  courses?: ICourseData[];
  /** Whether to use authenticated API calls */
  withAuthentication?: boolean;
}

/**
 * Result type returned by the useCategory hook
 * @interface UseCategoryResult
 */
interface UseCategoryResult {
  /** Filtered course items based on applied filters */
  filteredCategoryItems: ICourseData[];
  /** Loading state for catalog data */
  loadingCategoryCatalogData: boolean;
  /** Current applied filters */
  categoryFilters: CourseDataFilters;
  /** Whether more items can be loaded */
  hasMore: boolean;
  /** Current page number */
  page: number;
  /** Set page number */
  setPage: (page: number) => void;
  /** Handle category change */
  handleCategory: (params: { categoryName?: string; filters: Partial<CourseDataFilters> }) => void;
  /** Handle instrument filter change */
  instrumentsCategoryFilterOnChange: (checked: boolean, instrument: Instrument) => void;
  /** Handle skill level filter change */
  skillLevelCategoryFilterOnChange: (checked: boolean, skillLevel: string) => void;
  /** Handle musical genre filter change */
  musicalGenreCategoryFilterOnChange: (checked: boolean, genre: string) => void;
  /** Handle topic filter change */
  topicCategoryFilterOnChange: (checked: boolean, topic: string) => void;
  /** Handle course type filter change */
  courseTypeCategoryFilterOnChange: (checked: boolean, courseType: CourseType) => void;
  /** Parent category instruments */
  parentCategoryInstruments: Instrument[];
  /** Ref callback for the last course element (for intersection observer) */
  lastCourseElementRef: (node: HTMLElement | null) => void;
  /** Load more items */
  loadMore: () => void;
  /** Handle skill levels change (bulk update) */
  handleSkillLevelsChange: (selectedLevels: string[] | null) => void;
  /** Handle genre change */
  handleGenresChange: (genre: string, checked: boolean) => void;
  /** Handle topic change */
  handleTopicsChange: (topic: string, checked: boolean) => void;
  /** Handle type change */
  handleTypeChange: (type: string, checked: boolean) => void;
  /** Reset all sidebar filters */
  onResetSidebarFilters: () => void;
  /** Whether any filter is currently active */
  isAnyFilterActive: boolean;
  /** Clear results and reset pagination */
  clearResults: (isRemovingLastFilter?: boolean, isFirstFilter?: boolean) => void;
}

/**
 * Type for a filter key in CourseDataFilters
 */
type FilterKey = 'instruments' | 'skillLevels' | 'genres' | 'topics' | 'types';

/**
 * Normalizes course data to ensure consistent structure and default values
 *
 * @param courseData - Raw course data from API or other source
 * @returns Normalized course data with consistent structure and default values
 */
function normalizeCourseData(courseData: any): ICourseData {
  // If not a valid object, return a fallback object
  if (!courseData || typeof courseData !== 'object') {
    return {
      documentId: 'invalid-item',
      title: 'Elemento non valido',
      courseType: 'masterclass',
      description: '',
      duration: 0,
      imageSrc: '/assets/images/card-placeholder.png',
      thumbnailCard: '/assets/images/card-placeholder.png',
      videoCount: 0,
      coursePercentage: 0,
      genres: [],
      instructors: [],
      highlighted: false,
      isCourseStarted: false,
      isLocked: false,
      isUpcoming: false,
      isPlayable: false,
      linkLive: undefined,
      date: undefined,
    };
  }

  // Normalize the existing fields
  return {
    documentId: courseData.documentId || 'unknown',
    title: courseData.title || 'Titolo non disponibile',
    courseType: courseData.courseType || 'masterclass',
    description: courseData.description || [], // Using empty array instead of null
    duration: typeof courseData.duration === 'number' ? courseData.duration : 0,
    imageSrc: courseData.imageSrc || '/assets/images/card-placeholder.png',
    thumbnailCard: courseData.thumbnailCard || '/assets/images/card-placeholder.png',
    videoCount: typeof courseData.videoCount === 'number' ? courseData.videoCount : 0,
    coursePercentage:
      typeof courseData.coursePercentage === 'number' ? courseData.coursePercentage : 0,
    genres: Array.isArray(courseData.genres) ? courseData.genres : [],
    instructors: Array.isArray(courseData.instructors)
      ? courseData.instructors.map((instructor: any) => ({
          name: instructor.name || 'Istruttore',
          avatarUrl: instructor.avatarUrl || '/assets/images/default-avatar.png',
        }))
      : [],
    level: courseData.level,
    highlighted: !!courseData.highlighted,
    isCourseStarted: !!courseData.isCourseStarted,
    isLocked: !!courseData.isLocked,
    isFavourite: !!courseData.isFavourite,
    favouriteId: courseData.favouriteId || undefined,
    seasons: courseData.seasons || [],
    lessons: courseData.lessons || [],
    trailerPlaybackId: courseData.trailerPlaybackId || undefined,
    trailerPlaybackToken: courseData.trailerPlaybackToken || undefined,
    isPurchasable: courseData.isPurchasable || false,
    progress: courseData.progress || 0,
    requirements: courseData.requirements || [],
    stripeLink: courseData.stripeLink || undefined,
    upComingEvent: courseData.upComingEvent || undefined,
    isUpcoming: courseData.isUpcoming || false,
    isPlayable: courseData.isPlayable || false,
    linkLive: courseData.linkLive || undefined,
    date: courseData.date || undefined,
  };
}

/**
 * Helper function to extract data from various response structures
 *
 * @param value - API response value
 * @returns Array of course data items
 */
function extractResponseData(value: any): any[] {
  // 1. If value is an array
  if (Array.isArray(value)) {
    return value;
  }
  // 2. If value has a 'data' property that's an array
  else if (value && typeof value === 'object' && 'data' in value && Array.isArray(value.data)) {
    return value.data;
  }
  // 3. If value.data has a 'data' property that's an array (nested structure)
  else if (
    value &&
    typeof value === 'object' &&
    'data' in value &&
    value.data &&
    typeof value.data === 'object' &&
    'data' in value.data &&
    Array.isArray(value.data.data)
  ) {
    return value.data.data;
  }

  return [];
}

/**
 * Helper function to check if an item is a valid course item
 *
 * @param item - Item to check
 * @returns boolean indicating if the item is a valid course item
 */
function isValidCourseItem(item: any): boolean {
  return (
    item &&
    typeof item === 'object' &&
    'documentId' in item &&
    'title' in item &&
    'courseType' in item
  );
}

/**
 * Helper function to merge arrays of items, avoiding duplicates
 *
 * @param prevItems - Previous items array
 * @param newItems - New items to add
 * @returns Merged array with duplicates removed
 */
function mergeUniqueItems(prevItems: ICourseData[], newItems: ICourseData[]): ICourseData[] {
  // Create a Set of previous documentIds for O(1) lookup
  const prevIds = new Set(prevItems.map((item) => item.documentId));

  // Filter out items that already exist in prevItems
  const uniqueNewItems = newItems.filter((item) => !prevIds.has(item.documentId));

  // If no new unique items, return original array (maintain reference equality)
  if (uniqueNewItems.length === 0) {
    return prevItems;
  }

  // Return new array with unique items appended
  return [...prevItems, ...uniqueNewItems];
}

// Cache to store API responses
const categoryDataCache = new Map<string, any>();

/**
 * Hook for managing course category data, filtering, and pagination
 *
 * This hook handles filtering course data by various criteria (instruments,
 * skill levels, genres, topics, course types), loading data from API based on
 * those filters, and managing pagination for infinite scrolling. It supports
 * both authenticated and non-authenticated user scenarios.
 *
 * @param props - Configuration options for the hook
 * @returns Object containing filtered data, loading state, filter handlers, and pagination controls
 */
export function useCategory({
  eventBus,
  filters,
  categoryName,
  courses,
  withAuthentication = false,
}: UseCategoryProps): UseCategoryResult {
  const { user } = useAuthStore();
  const { fetchWithAuth } = useAuthenticatedFetch();

  // Initialize with normalized courses if available, empty array otherwise
  const initialCourses = useMemo(() => {
    if (courses && Array.isArray(courses)) {
      return courses.map(normalizeCourseData);
    }
    return [];
  }, [courses]);

  const [filteredCategoryItems, setFilteredCategoryItems] = useState<ICourseData[]>(initialCourses);
  const [loadingCategoryCatalogData, setLoadingCategoryCatalogData] = useState(false);
  const [categoryFilters, setCategoryFilters] = useState<CourseDataFilters>({
    instruments: [],
    skillLevels: [],
    genres: [],
    topics: [],
    types: [],
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 25;

  // Memoize parent instruments to avoid recalculation on every render
  const parentCategoryInstruments = useMemo(() => {
    return filters.instruments.filter((inst) => !inst.parentInstrument);
  }, [filters.instruments]);

  // References for tracking component state
  const isMounted = useRef(true);
  const currentRequestIdRef = useRef<string>('');
  const fetchCompletedRef = useRef<boolean>(false);
  const removingLastFilterRef = useRef<boolean>(false);
  const loadMoreTimeout = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Performance tracking - uncomment for debugging
  // const renderCount = useRef(0);
  // useEffect(() => {
  //   renderCount.current += 1;
  //   console.log(`useCategory render count: ${renderCount.current}`);
  // });

  // Create authenticated services with token refresh - memoized to avoid recreation
  const authServices = useMemo(
    () => createAuthenticatedCatalogServices(fetchWithAuth, eventBus),
    [fetchWithAuth, eventBus],
  );

  /**
   * Checks if a filter change will result in no active filters
   *
   * @param updatedFilters - Partial filters that would be applied
   * @returns boolean indicating if all filters would be empty after update
   */
  const willHaveNoActiveFilters = useCallback(
    (updatedFilters: Partial<CourseDataFilters>) => {
      const mergedFilters = { ...categoryFilters, ...updatedFilters };
      return Object.values(mergedFilters).every((arr) => !Array.isArray(arr) || arr.length === 0);
    },
    [categoryFilters],
  );

  /**
   * Determines if any non-instrument filter is currently active
   */
  const isAnyFilterActive = useMemo(() => {
    return (
      categoryFilters.skillLevels.length > 0 ||
      categoryFilters.genres.length > 0 ||
      categoryFilters.topics.length > 0 ||
      categoryFilters.types.length > 0
    );
  }, [
    categoryFilters.skillLevels,
    categoryFilters.genres,
    categoryFilters.topics,
    categoryFilters.types,
  ]);

  /**
   * Clears the current results and resets pagination
   *
   * @param isRemovingLastFilter - Whether this clear is due to removing the last filter
   * @param isFirstFilter - Whether this is the first filter being applied
   */
  const clearResults = useCallback(
    (isRemovingLastFilter = false, isFirstFilter = false) => {
      // For rapid filter changes, we want to keep showing loading state
      setLoadingCategoryCatalogData(true);

      setPage(1);
      setHasMore(true);

      // Special handling for authenticated users removing the last filter
      if (isRemovingLastFilter && withAuthentication) {
        setFilteredCategoryItems([]);
        removingLastFilterRef.current = true;
        fetchCompletedRef.current = false;
        currentRequestIdRef.current = '';
        return;
      }

      // Check if we should clear items
      const hasActiveFilters = Object.values(categoryFilters).some(
        (arr) => Array.isArray(arr) && arr.length > 0,
      );

      if (!isRemovingLastFilter && (isFirstFilter || hasActiveFilters)) {
        setFilteredCategoryItems([]);
      }

      // If removing last filter for non-authenticated users, just mark it
      if (isRemovingLastFilter) {
        removingLastFilterRef.current = true;
      }
    },
    [categoryFilters, withAuthentication],
  );

  /**
   * Generic handler for filter changes that consolidates common logic
   * Memoized to maintain reference equality
   */
  const handleFilterChange = useCallback(
    (filterKey: FilterKey, newValues: any[], isFirstFilter: boolean) => {
      // Check if this change will result in no active filters
      const updatedFilters = { [filterKey]: newValues } as Partial<CourseDataFilters>;
      const willBeEmpty = willHaveNoActiveFilters(updatedFilters);

      // Handle removing the last filter differently
      // Safely check if the current filter is an array and get its length
      const currentFilter = categoryFilters[filterKey];
      const currentFilterLength = Array.isArray(currentFilter) ? currentFilter.length : 0;

      const isRemovingLastFilter = currentFilterLength > 0 && newValues.length === 0 && willBeEmpty;

      // Clear results with appropriate flags
      clearResults(isRemovingLastFilter, isFirstFilter);

      // Update the filter state
      setCategoryFilters((prev) => ({
        ...prev,
        [filterKey]: newValues,
      }));

      // Always reset to first page after filter change
      setPage(1);
    },
    [categoryFilters, clearResults, willHaveNoActiveFilters],
  );

  /**
   * Handles category and filter changes
   * Memoized to prevent unnecessary recreations
   */
  const handleCategory = useCallback(
    ({
      categoryName: incomingQuery,
      filters: incomingFilters,
    }: {
      categoryName?: string;
      filters: Partial<CourseDataFilters>;
    }) => {
      if (!incomingQuery) return;

      // Clear results and set loading state before changing filters
      clearResults();

      setCategoryFilters((prev) => ({
        ...prev,
        ...incomingFilters,
      }));
    },
    [clearResults],
  );

  /**
   * Effect to fetch category data when filters, page, or category changes
   * Optimized with proper dependency management
   */
  useEffect(() => {
    // Reset isMounted at the beginning of effect
    isMounted.current = true;

    if (!categoryName) return;

    // Check if we're removing the last filter
    const isRemovingLastFilter = removingLastFilterRef.current;

    // Check if any filters are active
    const hasActiveFilters = Object.values(categoryFilters).some(
      (arr) => Array.isArray(arr) && arr.length > 0,
    );

    // Skip first page load for authenticated users under certain conditions
    if (
      withAuthentication &&
      user?.email &&
      page === 1 &&
      !hasActiveFilters &&
      initialCourses.length > 0 &&
      !isRemovingLastFilter &&
      fetchCompletedRef.current // Only skip if we've already fetched once
    ) {
      setLoadingCategoryCatalogData(false);
      return;
    }

    // Always set loading state
    setLoadingCategoryCatalogData(true);

    // Abort any in-flight request before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Generate a unique request ID to prevent race conditions
    const requestId = Date.now().toString();
    currentRequestIdRef.current = requestId;

    // Create a cache key for this request
    const cacheKey = JSON.stringify({
      categoryName,
      filters: categoryFilters,
      page,
      withAuthentication,
      userEmail: user?.email,
    });

    // Check if we have a cached response
    if (categoryDataCache.has(cacheKey) && page === 1) {
      const cachedData = categoryDataCache.get(cacheKey);
      setFilteredCategoryItems(cachedData);
      setLoadingCategoryCatalogData(false);
      fetchCompletedRef.current = true;
      removingLastFilterRef.current = false;
      return;
    }

    // Use timeout to ensure this runs after all synchronous state updates
    const timeoutId = setTimeout(async () => {
      try {
        // If this is not the current request, abort
        if (currentRequestIdRef.current !== requestId || !isMounted.current) {
          return;
        }

        let result: Either<Error, any>;

        // Choose between authenticated and non-authenticated data fetch
        if (withAuthentication && user?.email) {
          console.log('Category Fetch - Using authenticated service');
          const apiResult = await authServices.getUserCategoryFilteredData(
            user.email,
            categoryFilters,
            { page, pageSize },
            categoryName as CategoryTypes,
          );

          if (!apiResult.success) {
            result = left(apiResult.error || new Error('Failed to fetch data'));
          } else {
            result = { _tag: 'Right', value: apiResult.data as any };
          }
        } else {
          console.log('Category Fetch - Using non-authenticated service');
          result = await getCategoryFilteredData(
            categoryFilters,
            { page, pageSize },
            categoryName,
          )();
        }

        // Verify that the component is still mounted and this is still the current request
        if (!isMounted.current || currentRequestIdRef.current !== requestId) return;

        if (result._tag === 'Left') {
          if (page === 1) setFilteredCategoryItems([]);
          setHasMore(false);
        } else {
          // Extract data from the response
          const responseData = extractResponseData(result.value);

          // Process the data
          const processedData = responseData.filter(isValidCourseItem).map(normalizeCourseData);

          // Cache the result for future use (only for page 1)
          if (page === 1) {
            categoryDataCache.set(cacheKey, processedData);
          }

          // Only set data if this is still the most recent request
          if (isMounted.current && currentRequestIdRef.current === requestId) {
            setFilteredCategoryItems((prev) => {
              if (page === 1) {
                return processedData;
              } else {
                // Append new items, avoiding duplicates
                return mergeUniqueItems(prev, processedData);
              }
            });

            setHasMore(processedData.length >= pageSize / 2);
          }
        }

        // Mark fetch as completed
        fetchCompletedRef.current = true;
        // Reset removing last filter flag
        removingLastFilterRef.current = false;
      } catch (error) {
        console.error('Error in category fetch:', error);
        // Verify that the component is still mounted
        if (!isMounted.current || currentRequestIdRef.current !== requestId) return;

        if (page === 1) setFilteredCategoryItems([]);
        setHasMore(false);
      } finally {
        // Verify that the component is still mounted and this is the current request
        if (isMounted.current && currentRequestIdRef.current === requestId) {
          setLoadingCategoryCatalogData(false);
        }
      }
    }, 0); // Zero timeout ensures this runs after synchronous operations

    // Cleanup on unmount or dependency change
    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    categoryName,
    page,
    withAuthentication,
    user?.email,
    authServices,
    pageSize,
    initialCourses.length,
    // Use JSON.stringify to create stable reference for complex objects
    JSON.stringify(categoryFilters),
  ]);

  /**
   * Handles instrument filter changes
   * Memoized to maintain reference equality between renders
   */
  const instrumentsCategoryFilterOnChange = useCallback(
    (checked: boolean, instrument: Instrument) => {
      const newInstruments = updateInstrumentsFilter(
        checked,
        instrument,
        categoryFilters.instruments,
        filters.instruments,
      );

      const isFirstFilter =
        categoryFilters.instruments.length === 0 && newInstruments.length > 0 && !isAnyFilterActive;

      handleFilterChange('instruments', newInstruments, isFirstFilter);
    },
    [categoryFilters.instruments, filters.instruments, handleFilterChange, isAnyFilterActive],
  );

  /**
   * Handles skill level filter changes
   * Memoized to maintain reference equality between renders
   */
  const skillLevelCategoryFilterOnChange = useCallback(
    (checked: boolean, skillLevel: string) => {
      const newSkillLevels = toggleFilterValue(categoryFilters.skillLevels, skillLevel, checked);

      const isFirstFilter =
        categoryFilters.skillLevels.length === 0 && newSkillLevels.length > 0 && !isAnyFilterActive;

      handleFilterChange('skillLevels', newSkillLevels, isFirstFilter);
    },
    [categoryFilters.skillLevels, handleFilterChange, isAnyFilterActive],
  );

  /**
   * Handles genre filter changes
   * Memoized to maintain reference equality between renders
   */
  const musicalGenreCategoryFilterOnChange = useCallback(
    (checked: boolean, genre: string) => {
      const newGenres = toggleFilterValue(categoryFilters.genres, genre, checked);

      const isFirstFilter =
        categoryFilters.genres.length === 0 && newGenres.length > 0 && !isAnyFilterActive;

      handleFilterChange('genres', newGenres, isFirstFilter);
    },
    [categoryFilters.genres, handleFilterChange, isAnyFilterActive],
  );

  /**
   * Handles topic filter changes
   * Memoized to maintain reference equality between renders
   */
  const topicCategoryFilterOnChange = useCallback(
    (checked: boolean, topic: string) => {
      const newTopics = toggleFilterValue(categoryFilters.topics, topic, checked);

      const isFirstFilter =
        categoryFilters.topics.length === 0 && newTopics.length > 0 && !isAnyFilterActive;

      handleFilterChange('topics', newTopics, isFirstFilter);
    },
    [categoryFilters.topics, handleFilterChange, isAnyFilterActive],
  );

  /**
   * Handles course type filter changes
   * Memoized to maintain reference equality between renders
   */
  const courseTypeCategoryFilterOnChange = useCallback(
    (checked: boolean, courseType: CourseType) => {
      const newTypes = toggleFilterValue(
        categoryFilters.types,
        courseType,
        checked,
      ) as CourseType[];

      const isFirstFilter =
        categoryFilters.types.length === 0 && newTypes.length > 0 && !isAnyFilterActive;

      handleFilterChange('types', newTypes, isFirstFilter);
    },
    [categoryFilters.types, handleFilterChange, isAnyFilterActive],
  );

  /**
   * Bulk updates skill level filters
   * Memoized to maintain reference equality between renders
   */
  const handleSkillLevelsChange = useCallback(
    (selectedLevels: string[] | null) => {
      const newSkillLevels = selectedLevels || [];

      const isFirstFilter =
        categoryFilters.skillLevels.length === 0 && newSkillLevels.length > 0 && !isAnyFilterActive;

      handleFilterChange('skillLevels', newSkillLevels, isFirstFilter);
    },
    [categoryFilters.skillLevels, handleFilterChange, isAnyFilterActive],
  );

  /**
   * Alternative interface for handling genre changes
   * Memoized to maintain reference equality between renders
   */
  const handleGenresChange = useCallback(
    (genre: string, checked: boolean) => {
      musicalGenreCategoryFilterOnChange(checked, genre);
    },
    [musicalGenreCategoryFilterOnChange],
  );

  /**
   * Alternative interface for handling topic changes
   * Memoized to maintain reference equality between renders
   */
  const handleTopicsChange = useCallback(
    (topic: string, checked: boolean) => {
      topicCategoryFilterOnChange(checked, topic);
    },
    [topicCategoryFilterOnChange],
  );

  /**
   * Alternative interface for handling course type changes
   * Memoized to maintain reference equality between renders
   */
  const handleTypeChange = useCallback(
    (type: string, checked: boolean) => {
      courseTypeCategoryFilterOnChange(checked, type as CourseType);
    },
    [courseTypeCategoryFilterOnChange],
  );

  /**
   * Loads more items when scrolling
   * Memoized with debounce logic to prevent rapid multiple calls
   */
  const loadMore = useCallback(() => {
    if (!loadingCategoryCatalogData && hasMore && fetchCompletedRef.current) {
      // Only load more if not currently loading, has more items, and previous fetch completed
      setPage((prev) => prev + 1);
    }
  }, [loadingCategoryCatalogData, hasMore]);

  /**
   * Observer setup for infinite scroll with optimized logic
   */
  const observer = useRef<IntersectionObserver | null>(null);
  const lastCourseElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loadingCategoryCatalogData) return;

      // Cleanup previous observer
      if (observer.current) observer.current.disconnect();

      // Create new observer with better options for performance
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            // Add debounce to prevent multiple rapid calls
            if (!loadMoreTimeout.current) {
              loadMoreTimeout.current = setTimeout(() => {
                loadMore();
                loadMoreTimeout.current = null;
              }, 150);
            }
          }
        },
        { rootMargin: '300px' },
      ); // Preload earlier for smoother experience

      if (node) observer.current.observe(node);
    },
    [loadingCategoryCatalogData, hasMore, loadMore],
  );

  /**
   * Resets all sidebar filters
   * Memoized to maintain reference equality between renders
   */
  const onResetSidebarFilters = useCallback(() => {
    if (withAuthentication) {
      // For authenticated users, mark as removing last filter
      clearResults(true);
    } else {
      // For guest users, clear and restore initial data
      clearResults();
    }

    setCategoryFilters((prev) => ({
      ...prev,
      skillLevels: [],
      genres: [],
      topics: [],
      types: [],
    }));

    // For guest users, immediately restore initial data
    if (!withAuthentication && initialCourses.length > 0) {
      setFilteredCategoryItems(initialCourses);
      setLoadingCategoryCatalogData(false);
    }

    setPage(1); // Reset to first page
  }, [clearResults, withAuthentication, initialCourses]);

  // Clean up resources when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup function to run on unmount
      isMounted.current = false;
      if (loadMoreTimeout.current) {
        clearTimeout(loadMoreTimeout.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return {
    filteredCategoryItems,
    loadingCategoryCatalogData,
    categoryFilters,
    hasMore,
    page,
    setPage,
    handleCategory,
    instrumentsCategoryFilterOnChange,
    skillLevelCategoryFilterOnChange,
    musicalGenreCategoryFilterOnChange,
    topicCategoryFilterOnChange,
    courseTypeCategoryFilterOnChange,
    parentCategoryInstruments,
    lastCourseElementRef,
    loadMore,
    handleSkillLevelsChange,
    handleGenresChange,
    handleTopicsChange,
    handleTypeChange,
    onResetSidebarFilters,
    isAnyFilterActive,
    clearResults,
  };
}
